export type OverviewCountType = 'done' | 'updated' | 'created' | 'due';

export const SectionIds = {
	RecentActivity: 'activityFeed',
	PriorityBreakdown: 'breakdownByPriority',
	StatusOverview: 'progressByStatus',
	TeamWorkload: 'workloadByAssignee',
	TypesOfWork: 'typesOfWork',
	OverviewCard: 'overviewCard',
	EpicProgress: 'epicProgress',
	RelatedProjects: 'relatedProjects',
} as const;

export type SectionId = (typeof SectionIds)[keyof typeof SectionIds];

export const BoundaryIds = {
	OverviewDone: 'jira-summary-overview-done-count',
	OverviewUpdated: 'jira-summary-overview-updated-count',
	OverviewCreated: 'jira-summary-overview-created-count',
	OverviewDue: 'jira-summary-overview-due-count',
	StatusOverview: 'jira-summary-status-overview',
	CsatOverTime: 'jira-summary-csat-over-time',
	PriorityBreakdown: 'jira-summary-priority-breakdown',
	RecentActivity: 'jira-summary-recent-activity',
	TypesOfWork: 'jira-summary-types-of-work',
	TeamWorkload: 'jira-summary-team-workload',
	EpicProgress: 'jira-summary-epic-progress',
	RelatedProjects: 'jira-summary-related-projects',
} as const;

export const overviewCountBoundaryIds: Record<OverviewCountType, OverviewSectionBoundaryId> = {
	done: BoundaryIds.OverviewDone,
	updated: BoundaryIds.OverviewUpdated,
	created: BoundaryIds.OverviewCreated,
	due: BoundaryIds.OverviewDue,
};

export type SectionBoundaryId = (typeof BoundaryIds)[keyof typeof BoundaryIds];

export type OverviewSectionBoundaryId = Extract<
	SectionBoundaryId,
	| typeof BoundaryIds.OverviewDone
	| typeof BoundaryIds.OverviewUpdated
	| typeof BoundaryIds.OverviewCreated
	| typeof BoundaryIds.OverviewDue
>;

export const MetricTypes = {
	DISTRIBUTION: 'DISTRIBUTION',
	COUNT: 'COUNT',
} as const;

export type MetricType = (typeof MetricTypes)[keyof typeof MetricTypes];

export type CountMetric = {
	metricType: typeof MetricTypes.COUNT;
	maxCount: number;
};

export type DistributionMetric = {
	metricType: typeof MetricTypes.DISTRIBUTION;
	maxCount: undefined;
};

/**
 * Color scheme for the chart
 */

export const ColorSchemes = {
	DEFAULT: 'DEFAULT',
	BLUE: 'BLUE',
} as const;

export type ColorScheme = (typeof ColorSchemes)[keyof typeof ColorSchemes];

/**
 * Activity feed types
 */
export const ActivityTypes = {
	STATUS_CHANGED: 'STATUS_CHANGED',
	COMMENT_ADDED: 'COMMENT_ADDED',
	ATTACHMENT_ADDED: 'ATTACHMENT_ADDED',
	PRIORITY_CHANGED: 'PRIORITY_CHANGED',
	ASSIGNEE_CHANGED: 'ASSIGNEE_CHANGED',
	SINGLE_FIELD_UPDATED: 'SINGLE_FIELD_UPDATED',
	CREATED: 'CREATED',
} as const;

export type ActivityType = (typeof ActivityTypes)[keyof typeof ActivityTypes];

type ActivityDataMap = {
	STATUS_CHANGED: StatusChangedData;
	COMMENT_ADDED: CommentAddedData;
	ATTACHMENT_ADDED: AttachmentAddedData;
	PRIORITY_CHANGED: PriorityChangedData;
	ASSIGNEE_CHANGED: AssigneeChangedData;
	SINGLE_FIELD_UPDATED: SingleFieldUpdatedData;
	CREATED: IssueCreatedData;
};

export interface Activity<T extends ActivityType> {
	created: number;
	issueData: IssueData;
	author: Author;
	type: T;
	data: ActivityDataMap[T];
}

export type IssueData = {
	summary: string;
	icon: string;
	id: number;
	url: string;
	key: string;
};

export type Author = {
	accountId: string;
	displayName: string;
	profilePicture: string | null;
};

/**
 * Activity data types
 */
interface AttachmentAddedData {
	url: string;
}

type StatusChangedData = {
	oldStatus: string;
	newStatus: string;
};

type CommentAddedData = {
	comment: string;
};

type PriorityChangedData = {
	fromName: string;
	toName: string;
	fromIconUrl: string;
	toIconUrl: string;
};

type AssigneeChangedData = {
	fromAssignee: Author;
	toAssignee: Author | null;
};

type UpdatedCustomFieldData = {
	fieldname: string;
	oldValue: string;
	newValue: string;
};

type SingleFieldUpdatedData = UpdatedCustomFieldData;

type IssueCreatedData = {};
